import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Phone, 
  Search, 
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  RefreshCw,
  MessageSquare,
  CalendarClock,
  User,
  PhoneForwarded,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';

const FollowupCallsPage = ({ user, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Filters
  const [dateFilterType, setDateFilterType] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedService, setSelectedService] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  
  // Modals
  const [rescheduleAppointmentModalOpen, setRescheduleAppointmentModalOpen] = useState(false);
  const [rescheduleCallModalOpen, setRescheduleCallModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false);
  
  // Selected appointment for actions
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Form states
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleCallDate, setRescheduleCallDate] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [appointmentNotes, setAppointmentNotes] = useState('');

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get date ranges
  const getDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilterType) {
      case 'today':
        return { start: formatLocalDate(today), end: formatLocalDate(today) };
      case 'tomorrow': {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        return { start: formatLocalDate(tomorrow), end: formatLocalDate(tomorrow) };
      }
      case 'this_week': {
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));
        return { start: formatLocalDate(today), end: formatLocalDate(weekEnd) };
      }
      case 'this_month': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: formatLocalDate(monthStart), end: formatLocalDate(monthEnd) };
      }
      case 'custom':
        return { start: customStartDate, end: customEndDate };
      default:
        return { start: formatLocalDate(today), end: formatLocalDate(today) };
    }
  }, [dateFilterType, customStartDate, customEndDate]);

  // Fetch appointments for followup (only scheduled and rescheduled)
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      if (!start || !end) {
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams({
        start_date: start,
        end_date: end
      });
      
      if (selectedService !== 'all') {
        params.append('service_id', selectedService);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const response = await api.get(`/followup-calls/appointments?${params}`);
      setAppointments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, selectedService, searchQuery, statusFilter]);

  // Fetch services
  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Confirm appointment
  const confirmAppointment = async (appointmentId) => {
    try {
      await api.put(`/appointments/${appointmentId}`, { status: 'confirmed' });
      toast.success('Appointment confirmed!');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm appointment');
    }
  };

  // Reschedule appointment
  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment || !rescheduleDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      const updateData = { 
        scheduled_date: rescheduleDate,
        status: 'rescheduled'
      };
      if (rescheduleTime) {
        updateData.scheduled_time = rescheduleTime;
      }
      await api.put(`/appointments/${selectedAppointment.id}`, updateData);
      toast.success('Appointment rescheduled!');
      setRescheduleAppointmentModalOpen(false);
      setRescheduleDate('');
      setRescheduleTime('');
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule appointment');
    }
  };

  // Reschedule followup call (updates the followup_call_date on appointment)
  const handleRescheduleCall = async () => {
    if (!selectedAppointment || !rescheduleCallDate) {
      toast.error('Please select a date');
      return;
    }
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, { 
        followup_call_date: rescheduleCallDate 
      });
      toast.success('Followup call rescheduled!');
      setRescheduleCallModalOpen(false);
      setRescheduleCallDate('');
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule call');
    }
  };

  // Cancel appointment
  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    if (!cancelNotes.trim()) {
      toast.error('Please add notes for cancellation');
      return;
    }
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, { 
        status: 'cancelled',
        remarks: `${selectedAppointment.remarks || ''}\n[Cancelled] ${cancelNotes}`.trim()
      });
      toast.success('Appointment cancelled');
      setCancelModalOpen(false);
      setCancelNotes('');
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel appointment');
    }
  };

  // Save notes
  const handleSaveNotes = async () => {
    if (!selectedAppointment) return;
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, { remarks: appointmentNotes });
      toast.success('Notes saved');
      setNotesModalOpen(false);
      setAppointmentNotes('');
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  // View on dashboard
  const viewOnDashboard = (appointment) => {
    navigate(`/?date=${appointment.scheduled_date}&highlight=${appointment.id}`);
  };

  // Open modals
  const openRescheduleAppointmentModal = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleDate(appointment.scheduled_date || '');
    setRescheduleTime(appointment.scheduled_time || '');
    setRescheduleAppointmentModalOpen(true);
  };

  const openRescheduleCallModal = (appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleCallDate(appointment.followup_call_date || appointment.scheduled_date || '');
    setRescheduleCallModalOpen(true);
  };

  const openCancelModal = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelNotes('');
    setCancelModalOpen(true);
  };

  const openNotesModal = (appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentNotes(appointment.remarks || '');
    setNotesModalOpen(true);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      rescheduled: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
      checked_in: 'bg-purple-100 text-purple-700',
      checked_out: 'bg-emerald-100 text-emerald-700',
      no_show: 'bg-gray-100 text-gray-700'
    };
    const labels = {
      scheduled: 'Scheduled',
      rescheduled: 'Rescheduled',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled',
      checked_in: 'Checked In',
      checked_out: 'Completed',
      no_show: 'No Show'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{labels[status] || status}</Badge>;
  };

  // Check if appointment is closed (actioned)
  const isClosedStatus = (status) => {
    return ['confirmed', 'cancelled', 'checked_in', 'checked_out', 'no_show'].includes(status);
  };

  // Filter label
  const getDateFilterLabel = () => {
    switch (dateFilterType) {
      case 'today': return 'Today';
      case 'tomorrow': return 'Tomorrow';
      case 'this_week': return 'This Week';
      case 'this_month': return 'This Month';
      case 'custom': return customStartDate && customEndDate ? `${customStartDate} to ${customEndDate}` : 'Custom Range';
      default: return 'Today';
    }
  };

  // Stats
  const scheduledCount = appointments.filter(a => a.status === 'scheduled').length;
  const rescheduledCount = appointments.filter(a => a.status === 'rescheduled').length;
  const closedCount = appointments.filter(a => isClosedStatus(a.status)).length;
  const pendingCount = scheduledCount + rescheduledCount;
  const totalCount = appointments.length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="w-6 h-6 text-blue-600" />
              Follow-up Calls
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {statusFilter === 'closed' 
                ? `${closedCount} closed calls`
                : `${pendingCount} pending • ${scheduledCount} scheduled • ${rescheduledCount} rescheduled`
              }
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date Filter */}
              <div>
                <Label className="text-xs text-gray-500">Date Range</Label>
                <Select value={dateFilterType} onValueChange={(v) => {
                  if (v === 'custom') {
                    setDateRangeModalOpen(true);
                  } else {
                    setDateFilterType(v);
                  }
                }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue>{getDateFilterLabel()}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="tomorrow">Tomorrow</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Status Filter */}
              <div>
                <Label className="text-xs text-gray-500">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Pending Calls" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending Calls</SelectItem>
                    <SelectItem value="scheduled">Scheduled Only</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled Only</SelectItem>
                    <SelectItem value="closed">Closed Calls</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Service Filter */}
              <div>
                <Label className="text-xs text-gray-500">Service</Label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Services" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Search */}
              <div>
                <Label className="text-xs text-gray-500">Search</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search name or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appointments List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
            <p className="text-gray-500 mt-2">Loading appointments...</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
            <Phone className="w-12 h-12 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-2">No appointments for selected period</p>
            <p className="text-xs text-gray-400">Try changing the date filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {appointments.map(appointment => (
              <Card 
                key={appointment.id} 
                className={`overflow-hidden ${
                  appointment.status === 'rescheduled' ? 'border-amber-200' : 'border-blue-200'
                }`}
              >
                <CardContent className="p-4">
                  {/* Header with Ref Number and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-mono text-gray-500">{appointment.ref_number || appointment.id.slice(0, 8)}</p>
                      <h3 className="font-semibold text-gray-900">{appointment.patient_name}</h3>
                    </div>
                    {getStatusBadge(appointment.status)}
                  </div>
                  
                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{appointment.patient_phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{appointment.scheduled_date}</span>
                      {appointment.scheduled_time && (
                        <span className="text-gray-400">@ {appointment.scheduled_time}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="w-4 h-4" />
                      <span className="truncate">{appointment.service_name}</span>
                    </div>
                    {appointment.remarks && (
                      <div className="flex items-start gap-2 text-sm text-gray-500">
                        <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{appointment.remarks}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                    {/* Confirm Button - Only for scheduled/rescheduled */}
                    {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
                      <Button
                        size="sm"
                        onClick={() => confirmAppointment(appointment.id)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Confirm
                      </Button>
                    )}
                    
                    {/* Reschedule Appointment Button */}
                    {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRescheduleAppointmentModal(appointment)}
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <CalendarClock className="w-4 h-4 mr-1" />
                        Reschedule
                      </Button>
                    )}
                    
                    {/* Cancel Button */}
                    {(appointment.status === 'scheduled' || appointment.status === 'rescheduled') && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCancelModal(appointment)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                    
                    {/* Reschedule Call Button - Only for pending appointments */}
                    {!isClosedStatus(appointment.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openRescheduleCallModal(appointment)}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        <PhoneForwarded className="w-4 h-4 mr-1" />
                        Reschedule Call
                      </Button>
                    )}
                    
                    {/* Notes Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openNotesModal(appointment)}
                      className="text-gray-600"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      Notes
                    </Button>
                    
                    {/* View on Dashboard Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewOnDashboard(appointment)}
                      className="text-indigo-600"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Reschedule Appointment Modal */}
        <Dialog open={rescheduleAppointmentModalOpen} onOpenChange={setRescheduleAppointmentModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reschedule Appointment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.patient_name}</p>
              </div>
              <div>
                <Label>Current Date</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.scheduled_date}</p>
              </div>
              <div>
                <Label>New Date *</Label>
                <Input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              {selectedAppointment?.service_type === 'time_based' && (
                <div>
                  <Label>New Time</Label>
                  <Input
                    type="time"
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRescheduleAppointmentModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRescheduleAppointment} className="bg-amber-600 hover:bg-amber-700">
                Reschedule Appointment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reschedule Call Modal */}
        <Dialog open={rescheduleCallModalOpen} onOpenChange={setRescheduleCallModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reschedule Follow-up Call</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.patient_name}</p>
              </div>
              <div>
                <Label>New Call Date *</Label>
                <Input
                  type="date"
                  value={rescheduleCallDate}
                  onChange={(e) => setRescheduleCallDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <p className="text-xs text-gray-500">
                This will move the appointment to a different follow-up call date without changing the actual appointment date.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRescheduleCallModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRescheduleCall} className="bg-blue-600 hover:bg-blue-700">
                Reschedule Call
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Appointment Modal */}
        <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Appointment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.patient_name}</p>
              </div>
              <div>
                <Label>Appointment Date</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.scheduled_date}</p>
              </div>
              <div>
                <Label>Cancellation Notes *</Label>
                <Textarea
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  placeholder="Please provide a reason for cancellation..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCancelModalOpen(false)}>
                Back
              </Button>
              <Button onClick={handleCancelAppointment} className="bg-red-600 hover:bg-red-700">
                Cancel Appointment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notes Modal */}
        <Dialog open={notesModalOpen} onOpenChange={setNotesModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Appointment Notes</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Patient</Label>
                <p className="text-sm text-gray-600">{selectedAppointment?.patient_name}</p>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={appointmentNotes}
                  onChange={(e) => setAppointmentNotes(e.target.value)}
                  placeholder="Add notes about this appointment..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotesModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveNotes}>
                Save Notes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom Date Range Modal */}
        <Dialog open={dateRangeModalOpen} onOpenChange={setDateRangeModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Date Range</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDateRangeModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                if (customStartDate && customEndDate) {
                  setDateFilterType('custom');
                  setDateRangeModalOpen(false);
                } else {
                  toast.error('Please select both dates');
                }
              }}>
                Apply
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FollowupCallsPage;
